import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Sparkles,
  Play,
  CheckCircle2,
  Star,
  BarChart3,
  Users,
  Zap,
  TrendingUp,
  Brain,
} from "lucide-react";

const HeroSection = () => {
  return (
    <section className="relative pt-28 pb-20 lg:pt-40 lg:pb-32 overflow-hidden bg-white">
      {/* Premium Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full bg-[radial-gradient(circle_at_50%_0%,rgba(56,189,248,0.08),rgba(255,255,255,0)_50%)]" />
        <div className="absolute top-[10%] right-[10%] w-[600px] h-[600px] bg-[radial-gradient(circle_at_center,rgba(13,148,136,0.15),rgba(255,255,255,0)_70%)] rounded-full animate-pulse-glow" />
        <div className="absolute bottom-[10%] left-[10%] w-[500px] h-[500px] bg-[radial-gradient(circle_at_center,rgba(59,130,246,0.15),rgba(255,255,255,0)_70%)] rounded-full animate-pulse-glow" style={{ animationDelay: '2s' }} />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.02)_1px,rgba(255,255,255,0)_1px),linear-gradient(90deg,rgba(0,0,0,0.02)_1px,rgba(255,255,255,0)_1px)] bg-[size:64px_64px] [-webkit-mask-image:radial-gradient(ellipse_at_center,black,rgba(0,0,0,0)_80%)] [mask-image:radial-gradient(ellipse_at_center,black,rgba(0,0,0,0)_80%)]" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left: Content */}
          <div className="text-center lg:text-left">
            {/* Announcement Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-50 border border-slate-200/60 text-slate-600 mb-8 shadow-sm"
            >
              <div className="flex h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-sm font-semibold tracking-tight flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5 text-accent" />
                AI Voice Screening Now Live
              </span>
              <span className="ml-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-accent text-white uppercase tracking-wider">New</span>
            </motion.div>

            {/* Headline */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="font-display text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight text-slate-900 mb-6 leading-[1.05]"
            >
              Better Hires. <br />
              <span className="italic text-accent">Faster</span> Decisions.
            </motion.h1>

            {/* Subheadline */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-lg md:text-xl text-slate-600 max-w-xl mx-auto lg:mx-0 mb-10 leading-relaxed font-medium"
            >
              The AI-first hiring platform that screens, scores, and shortlists candidates
              autonomously - so you focus on the humans worth talking to.
            </motion.p>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-col sm:flex-row items-center lg:items-start justify-center lg:justify-start gap-4 mb-10"
            >
              <Button variant="hero" size="xl" asChild className="relative overflow-hidden group min-w-0 sm:min-w-[200px]">
                <Link to="/auth?mode=signup">
                  <span className="relative z-10 flex items-center">
                    Start Hiring Free
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </span>
                  <div className="absolute inset-0 bg-white/10 translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
                </Link>
              </Button>
              <Button variant="hero-outline" size="xl" asChild className="group min-w-0 sm:min-w-[200px]">
                <Link to="/how-it-works">
                  <Play className="w-4 h-4 mr-2 text-accent fill-accent/20" />
                  Learn More
                </Link>
              </Button>
            </motion.div>

            {/* Social Proof Line */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.5 }}
              className="flex flex-wrap items-center justify-center lg:justify-start gap-x-6 gap-y-3"
            >
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-3.5 h-3.5 fill-accent text-accent" />
                ))}
                <span className="ml-1.5 text-xs font-bold text-slate-500">4.9/5</span>
              </div>
              <span className="text-xs font-bold text-slate-400">•</span>
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wide">Trusted by 7+ hiring teams</span>
              <span className="text-xs font-bold text-slate-400">•</span>
              <span className="flex items-center gap-1.5 text-xs font-bold text-slate-400 uppercase tracking-wide">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                Setup in 2 Minutes
              </span>
            </motion.div>
          </div>

          {/* Right: Dashboard Mockup */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative hidden lg:block"
          >
            <div className="relative">
              {/* Glow behind */}
              <div className="absolute -inset-4 bg-accent/5 rounded-[2.5rem] blur-2xl" />

              {/* Main Dashboard Card */}
              <div className="relative rounded-3xl bg-white border border-slate-200/60 shadow-2xl shadow-slate-200/50 p-6 overflow-hidden">
                {/* Header Bar */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-400" />
                    <div className="w-3 h-3 rounded-full bg-amber-400" />
                    <div className="w-3 h-3 rounded-full bg-emerald-400" />
                  </div>
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Live Dashboard</span>
                </div>

                {/* Stats Row */}
                <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-6">
                  {[
                    { label: "AI Score Avg", value: "87%", icon: Brain, color: "text-accent bg-accent/10" },
                    { label: "Screened Today", value: "142", icon: Users, color: "text-blue-500 bg-blue-500/10" },
                    { label: "Time Saved", value: "2.5h", icon: Zap, color: "text-amber-500 bg-amber-500/10" },
                  ].map((stat) => (
                    <div key={stat.label} className="rounded-2xl bg-slate-50/80 border border-slate-100 p-4">
                      <div className={`w-8 h-8 rounded-xl ${stat.color} flex items-center justify-center mb-2`}>
                        <stat.icon className="w-4 h-4" />
                      </div>
                      <p className="text-xl font-bold text-slate-900">{stat.value}</p>
                      <p className="text-[10px] font-medium text-slate-400 mt-0.5">{stat.label}</p>
                    </div>
                  ))}
                </div>

                {/* Activity Feed */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-bold text-slate-500">Recent Activity</span>
                    <span className="text-slate-400">Just now</span>
                  </div>
                  {[
                    { text: "Voice AI completed screening for 12 candidates", time: "2m ago", dot: "bg-emerald-500" },
                    { text: "Resume parser processed 28 applications", time: "15m ago", dot: "bg-blue-500" },
                    { text: "3 candidates auto-advanced to Round 2", time: "1h ago", dot: "bg-amber-500" },
                  ].map((activity) => (
                    <div key={activity.text} className="flex items-start gap-3 rounded-xl bg-slate-50/50 p-3 border border-slate-50">
                      <div className={`w-2 h-2 rounded-full ${activity.dot} mt-1.5 flex-shrink-0`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-slate-600 truncate">{activity.text}</p>
                        <p className="text-[10px] text-slate-400 mt-0.5">{activity.time}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Mini Chart */}
                <div className="mt-6 rounded-2xl bg-slate-50/80 border border-slate-100 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-bold text-slate-500">Hiring Velocity</span>
                    <span className="text-xs font-bold text-emerald-500 flex items-center gap-1">
                      <TrendingUp className="w-3 h-3" />+24%
                    </span>
                  </div>
                  <div className="flex items-end gap-1.5 h-12">
                    {[40, 60, 35, 80, 65, 90, 75, 95, 85, 100, 88, 92].map((h, i) => (
                      <motion.div
                        key={i}
                        initial={{ height: 0 }}
                        animate={{ height: `${h}%` }}
                        transition={{ duration: 0.5, delay: 0.6 + i * 0.05 }}
                        className={`flex-1 rounded-sm ${i >= 10 ? "bg-accent" : "bg-slate-200"}`}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Floating Badge */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 1, duration: 0.5 }}
                className="absolute -bottom-4 -left-4 rounded-2xl bg-white border border-slate-200/60 shadow-lg px-4 py-3 flex items-center gap-3"
              >
                <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-emerald-500" />
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-900">65% Faster</p>
                  <p className="text-[10px] text-slate-400">Avg. Time-to-Hire</p>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
